// Form templates — the page shell. Header action cluster, the
// Forms/Bookmarks/Archived tabs, the folders sidebar (toggled from the toolbar),
// the toolbar (search / two-pane Filter) and the templates DataTable with
// header-glyph sorting and pagination.

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  Tabs,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  FormBulkAction,
  FormDateFilter,
  FormFolderFilter,
  FormSortKey,
  useBookmarkFormsMutation,
  useBulkActionFormsMutation,
  useDeleteFormsMutation,
  useDuplicateFormMutation,
  useExportFormsMutation,
  useGetFormTemplatesQuery,
  useImportFormMutation,
  useUpdateFormsFolderMutation,
  type FormLifecycleStatus,
  type FormStateFilter,
  type FormStatusFilter,
  type FormTab,
  type FormTemplate,
} from '@/api/endpoints/formsApi';
import { useGetFormFoldersQuery } from '@/api/endpoints/formFoldersApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { DATE_ANY, buildDateFilterOptions, dateFilterOf, dateRangeOf } from '@/utils/dateFilterPresets';
import { parseFormImportJson } from './utils/formExportJson';
import { useFormTemplateColumns } from './formTemplateColumns';
import useModuleAccess, {
  useCanAccessFormResponses,
  useCanExportFormTemplates,
} from '@/hooks/useModuleAccess';
import FormFoldersPanel, { type FolderSelection } from './folders/FormFoldersPanel';
import FormFoldersModal from './folders/FormFoldersModal';
import CreateFormModal from './CreateFormModal';
import MoveToFolderModal from './folders/MoveToFolderModal';
import FormWorkflowModal from './workflows/FormWorkflowModal';
import FormResponsesModal from './response-sets/FormResponsesModal';

type SortType = 'asc' | 'desc';

// State filter values — the version-derived states; labels come from the
// shared forms_status_* keys. Lifecycle (Active/Suspended) is the separate
// Status category below.
const STATE_VALUES: FormStateFilter[] = ['new', 'draft', 'published'];
const STATUS_FILTER_VALUES: FormStatusFilter[] = ['active', 'suspended'];

// Filter option dots — semantic status tokens per value (ProjectsPage idiom).
const STATE_DOT: Record<FormStateFilter, string> = {
  new: 'var(--info)',
  draft: 'var(--text-3)',
  published: 'var(--brand)',
};
const STATUS_FILTER_DOT: Record<FormStatusFilter, string> = {
  active: 'var(--brand)',
  suspended: 'var(--warning)',
};

// Toolbar Filter category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['state', 'status', 'createdBy', 'publishedDate', 'lastUpdated'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// Tab entry state: the non-archived tabs PRE-SELECT Active (the request sends
// it anyway — the UI shows that honestly); Archived has no Status category.
const initialFilters = (tab: FormTab): Record<string, Set<string>> => ({
  ...emptyFilters(),
  status: tab === 'archived' ? new Set<string>() : new Set(['active']),
});

// Tab → API `status` (lifecycle). 'active' is inferred; 'archived' confirmed.
// Bookmarks is a filter, not a lifecycle: it fetches 'active' too and adds
// `bookmarked: true` on top.
const TAB_STATUS: Record<FormTab, FormLifecycleStatus> = {
  forms: 'active',
  bookmarks: 'active',
  archived: 'archived',
};

// Lifecycle verb → success-toast key (plural pairs resolved off `count`).
const ACTION_TOAST: Record<FormBulkAction, string> = {
  [FormBulkAction.ARCHIVE]: 'forms_archive_done',
  [FormBulkAction.RESTORE]: 'forms_restore_done',
  [FormBulkAction.SUSPEND]: 'forms_suspend_done',
  [FormBulkAction.ACTIVE]: 'forms_activate_done',
};

// Only the "away" moves confirm; Restore/Activate fire immediately (the
// Inspections split).
const CONFIRM_ACTIONS: FormBulkAction[] = [FormBulkAction.SUSPEND, FormBulkAction.ARCHIVE];

// The lifecycle status a verb leaves the form in — compared against the
// effective Status filter to decide whether the acted rows leave the current
// view (and the page-step-back guard applies).
const RESULT_STATUS: Record<FormBulkAction, FormLifecycleStatus> = {
  [FormBulkAction.ARCHIVE]: 'archived',
  [FormBulkAction.RESTORE]: 'active',
  [FormBulkAction.SUSPEND]: 'suspended',
  [FormBulkAction.ACTIVE]: 'active',
};

// UI sort key (sortable column headers) → backend orderBy.key. 'updated' is
// the default sort only — it has no sortable column.
const SORT_API_KEY: Record<string, FormSortKey> = {
  updated: FormSortKey.LastUpdated,
  name: FormSortKey.Title,
  published: FormSortKey.PublishedDate,
  lastEntry: FormSortKey.LastEntryDate,
  entries: FormSortKey.NumberOfEntries,
  status: FormSortKey.Status,
};

const FormTemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Verb gates (hidden, not disabled, when missing). Duplicate + Import ride
  // `create` (no forms import extra exists); the lifecycle verbs
  // (Suspend/Activate/Archive/Restore) ride `edit` (no extras either); Export
  // rides the module's `export` extra;
  // the Responses surface rides `responses_access`; the Workflow surface is a
  // whole OTHER module (`workflow`) and gates on ITS `view`. Bookmark stays
  // ungated — a per-user favourite, not a form mutation.
  const { canCreate, canEdit, canDelete } = useModuleAccess('forms_templates');
  const canExportForms = useCanExportFormTemplates();
  const canAccessResponses = useCanAccessFormResponses();
  const { canView: canViewWorkflows } = useModuleAccess('workflow');

  const [activeTab, setActiveTab] = useState<FormTab>('forms');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(() =>
    initialFilters('forms'),
  );
  // Custom-range FROM/TO per date category (`publishedDate` / `lastUpdated`).
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  const [sort, setSort] = useState<{ key: string; type: SortType }>({
    key: 'updated',
    type: 'desc',
  });
  // id → row: the selection bar's single-form actions need the row's own values,
  // and a row picked on page 1 is no longer in `items` after paging.
  const [selected, setSelected] = useState<Map<string, FormTemplate>>(new Map());
  const [createOpen, setCreateOpen] = useState(false);
  // A move in flight — from the selection bar (selection clears on success) or one
  // row's kebab (selection untouched). Carries the rows so the modal can derive
  // its subtitle and preselect their shared folder.
  const [moveTarget, setMoveTarget] = useState<{
    forms: FormTemplate[];
    fromSelection: boolean;
  } | null>(null);
  // A Suspend/Archive awaiting its ConfirmModal (Restore/Activate skip it).
  const [confirmAction, setConfirmAction] = useState<{
    ids: string[];
    action: FormBulkAction;
  } | null>(null);
  // A Delete awaiting its ConfirmModal — its own state, not folded into
  // `confirmAction`: delete is a separate endpoint, not a lifecycle verb.
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[] } | null>(null);
  // The folders sidebar is opt-in — the toolbar's Folders toggle reveals it.
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [folderSel, setFolderSel] = useState<FolderSelection>({ kind: 'all' });
  const [manageOpen, setManageOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [responsesOpen, setResponsesOpen] = useState(false);

  // Created-by options — fetched up-front so the pane is ready on first open.
  const { data: companyUsers = [] } = useGetUsersSelectionQuery();

  const filterCategories: FilterDropdownCategory[] = useMemo(() => {
    const dateOptions = buildDateFilterOptions(t);
    // Archived lists only archived forms — a lifecycle Status category makes
    // no sense there, so it exists on the other tabs only.
    const statusCategory: FilterDropdownCategory[] =
      activeTab === 'archived'
        ? []
        : [
            {
              key: 'status',
              label: t('forms_filter_status'),
              icon: 'activity',
              options: STATUS_FILTER_VALUES.map((s) => ({
                id: s,
                label: t(`forms_status_${s}`),
                dotColor: STATUS_FILTER_DOT[s],
              })),
            },
          ];
    return [
      {
        key: 'state',
        label: t('forms_filter_state'),
        icon: 'circle-dot',
        options: STATE_VALUES.map((s) => ({
          id: s,
          label: t(`forms_status_${s}`),
          dotColor: STATE_DOT[s],
        })),
      },
      ...statusCategory,
      {
        key: 'createdBy',
        label: t('text_created_by'),
        icon: 'user',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: companyUsers.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'publishedDate',
        label: t('forms_filter_published_date'),
        icon: 'calendar',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: dateOptions,
      },
      {
        key: 'lastUpdated',
        label: t('table_header_last_updated'),
        icon: 'history',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: dateOptions,
      },
    ];
  }, [t, companyUsers, activeTab]);

  const bookmarksTab = activeTab === 'bookmarks';
  const archivedTab = activeTab === 'archived';

  const publishedDate = dateFilterOf(filters, 'publishedDate');
  const lastUpdated = dateFilterOf(filters, 'lastUpdated');

  const { data, currentData, isLoading, isError, isFetching } = useGetFormTemplatesQuery(
    {
      // The Status filter's picks verbatim (Active is pre-selected on the
      // non-archived tabs); the tab's lifecycle value is the REQUIRED fallback
      // when the category is cleared (or absent, on Archived).
      status: filters.status.size
        ? ([...filters.status] as FormLifecycleStatus[])
        : [TAB_STATUS[activeTab]],
      page,
      limit,
      // Omitted (not false) on the other tabs: false would read as "only
      // non-bookmarked" and hide bookmarked forms from the default list.
      bookmarked: bookmarksTab || undefined,
      state: filters.state.size ? ([...filters.state] as FormStateFilter[]) : undefined,
      createdBy: filters.createdBy.size ? [...filters.createdBy] : undefined,
      // Folders sidebar → server narrowing. "All forms" sends nothing; a picked
      // folder sends its bare ids (self-sufficient — no folderType with it).
      folderType: folderSel.kind === 'unassigned' ? FormFolderFilter.UNASSIGNED : undefined,
      folder: folderSel.kind === 'folder' ? [folderSel.id] : undefined,
      publishedDate,
      publishedDateStart: dateRangeOf(dateRanges, 'publishedDate', publishedDate, 'start'),
      publishedDateEnd: dateRangeOf(dateRanges, 'publishedDate', publishedDate, 'end'),
      lastUpdated,
      lastUpdatedStart: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'start'),
      lastUpdatedEnd: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'end'),
      search: debouncedSearch || undefined,
      orderBy: {
        key: SORT_API_KEY[sort.key] ?? FormSortKey.CreatedAt,
        type: sort.type,
      },
    },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: folders = [],
    isLoading: foldersLoading,
    isError: foldersError,
  } = useGetFormFoldersQuery();

  // A new search / filter shrinks the result set — snap back to page 1.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges, folderSel, activeTab, sort]);

  // The selected folder can be deleted from the Manage-folders modal — fall
  // back to All forms once it is gone from the refreshed list.
  useEffect(() => {
    if (foldersLoading || folderSel.kind !== 'folder') return;
    if (!folders.some((f) => f.id === folderSel.id)) setFolderSel({ kind: 'all' });
  }, [folders, folderSel, foldersLoading]);

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent/0 on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Open a form in the builder — shared by the row click, the row kebab's Edit
  // and the selection bar's Edit.
  const openBuilder = useCallback(
    (form: FormTemplate) =>
      navigate('/form-templates/builder', {
        state: { formId: form.id, formTitle: form.name },
      }),
    [navigate],
  );

  // `bookmark` is false only from the Bookmarks tab, where every listed row is
  // bookmarked by definition of the filter — the one place the UI knows the
  // current state without a row-level flag. A run from the selection bar clears
  // the selection; a row run leaves it be — the split PeoplePage uses for bulk
  // actions.
  const [bookmarkForms, { isLoading: bookmarking }] = useBookmarkFormsMutation();
  const runBookmark = async (formIds: string[], fromSelection: boolean, bookmark = true) => {
    if (!formIds.length) return;
    try {
      await bookmarkForms({ formIds, bookmark }).unwrap();
      toast.success(
        t(bookmark ? 'forms_bookmark_done' : 'forms_unbookmark_done', { count: formIds.length }),
      );
      if (fromSelection) setSelected(new Map());
      // Removals drop the rows out of this tab on refetch. Emptying the current
      // page would leave `page` past the end — TableFooter recomputes its page
      // count but never calls onPage — so step back one.
      if (!bookmark && page > 1 && formIds.length >= items.length) setPage((p) => p - 1);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Lifecycle moves (PATCH /forms/bulk-action) — one runner behind the row
  // kebab AND the selection bar, the runBookmark split. On success the acted
  // ids are PRUNED from the selection (never a full clear): the Map caches each
  // row's `status`, which this call just rewrote — a bar run over the whole
  // selection prunes everything anyway. Returns success so the ConfirmModal can
  // stay open on failure.
  const [bulkActionForms, { isLoading: acting }] = useBulkActionFormsMutation();
  const runAction = async (ids: string[], action: FormBulkAction): Promise<boolean> => {
    try {
      await bulkActionForms({ formIds: ids, action }).unwrap();
      toast.success(t(ACTION_TOAST[action], { count: ids.length }));
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      // Step back a page only when the rows actually leave the current view —
      // i.e. the verb's resulting status falls outside the effective Status
      // filter. A suspend with the filter widened to include Suspended keeps
      // the rows in place, so stepping back would be wrong there.
      const effective = filters.status.size ? filters.status : new Set([TAB_STATUS[activeTab]]);
      if (!effective.has(RESULT_STATUS[action]) && page > 1 && ids.length >= items.length)
        setPage((p) => p - 1);
      return true;
    } catch {
      toast.error(t('error_generic'));
      return false;
    }
  };

  // Single entry point for both surfaces: Suspend/Archive detour through the
  // ConfirmModal, Restore/Activate fire immediately.
  const startAction = (ids: string[], action: FormBulkAction) => {
    if (!ids.length) return;
    if (CONFIRM_ACTIONS.includes(action)) setConfirmAction({ ids, action });
    else void runAction(ids, action);
  };

  const [updateFormsFolder, { isLoading: moving }] = useUpdateFormsFolderMutation();
  const runMove = async (folder: string | null) => {
    if (!moveTarget) return;
    try {
      await updateFormsFolder({ folder, forms: moveTarget.forms.map((f) => f.id) }).unwrap();
      toast.success(t('forms_move_folder_done', { count: moveTarget.forms.length }));
      if (moveTarget.fromSelection) setSelected(new Map());
      setMoveTarget(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Delete (POST /forms/bulk-delete) — ONE call behind the row kebab AND the
  // selection bar, so there is no fan-out and no partial-failure ambiguity. The
  // forms move to Trash, where they can be restored. Returns success so the
  // ConfirmModal can stay open on failure (runAction's contract).
  const [deleteForms, { isLoading: deleting }] = useDeleteFormsMutation();
  const runDelete = async (ids: string[]): Promise<boolean> => {
    try {
      await deleteForms({ formIds: ids }).unwrap();
      toast.success(t('forms_delete_done', { count: ids.length }));
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      // The rows are gone from this tab on refetch — emptying the current page
      // would leave `page` past the end, so step back one. Unlike runAction
      // there is no filter condition: a deleted row always leaves the view.
      if (page > 1 && ids.length >= items.length) setPage((p) => p - 1);
      return true;
    } catch {
      toast.error(t('error_generic'));
      return false;
    }
  };

  // Duplicate is additive (the CONFIRM_ACTIONS rule: only "away" moves
  // confirm), so it fires immediately — no dialog, like Bookmark and Import.
  // The copy surfaces via the list refetch; a bar-initiated run clears the
  // selection (the action on it is complete), a kebab run never touched it.
  const [duplicateForm, { isLoading: duplicating }] = useDuplicateFormMutation();
  const runDuplicate = async (form: FormTemplate, fromSelection = false) => {
    try {
      await duplicateForm(form.id).unwrap();
      toast.success(t('forms_duplicate_done', { name: form.name }));
      if (fromSelection) setSelected(new Map());
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Export queues a server-side job over the selection, or the whole listing
  // when nothing is selected ([] on the wire); the file arrives via the
  // notifications bell. Fire-and-forget like the People export. A selection
  // export clears the selection on success — the action on it is complete —
  // while a whole-listing run has nothing to clear.
  const [exportForms, { isLoading: exporting }] = useExportFormsMutation();
  const handleExport = () => {
    const formIds = [...selected.keys()];
    exportForms(formIds)
      .unwrap()
      .then(() => {
        toast.success(t('forms_export_started'));
        if (formIds.length) setSelected(new Map());
      })
      .catch(() => toast.error(t('error_generic')));
  };

  // Import — pick one of our Export JSON files and POST it verbatim (the wire
  // body IS the file shape; parseFormImportJson re-validates and drops any
  // foreign keys). Hidden-input idiom, same as the DataSource CSV import. The
  // input value resets in finally so re-picking the same file fires onChange.
  const [importForm, { isLoading: importing }] = useImportFormMutation();
  const importInputRef = useRef<HTMLInputElement>(null);
  const onPickImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || importing) return;
    try {
      // The input’s accept filter is advisory only — enforce the extension here.
      if (!file.name.toLowerCase().endsWith('.midani.json')) {
        toast.error(t('forms_import_invalid_file'));
        return;
      }
      let parsed;
      try {
        parsed = parseFormImportJson(await file.text());
      } catch {
        toast.error(t('forms_import_invalid_file'));
        return;
      }
      try {
        await importForm(parsed).unwrap();
        toast.success(t('forms_import_success'));
      } catch {
        toast.error(t('error_generic'));
      }
    } finally {
      e.target.value = '';
    }
  };

  const columns = useFormTemplateColumns({
    onEdit: canEdit ? openBuilder : undefined,
    onBookmark: (form) => runBookmark([form.id], false, !bookmarksTab),
    bookmarksTab,
    onMoveFolder: canEdit
      ? (form) => setMoveTarget({ forms: [form], fromSelection: false })
      : undefined,
    // Duplicate copies into the source's folder and the copy lands on the
    // Forms tab — off the Archived tab (the FormWorkflowModal rule). Deleting
    // a form is archive-first (the People rule): Delete exists on the
    // Archived tab only.
    onDuplicate: canCreate && !archivedTab ? (form) => runDuplicate(form) : undefined,
    onDelete: canDelete && archivedTab ? (form) => setConfirmDelete({ ids: [form.id] }) : undefined,
    // Lifecycle: Suspend↔Activate off the row's own status, Archive off the
    // Archived tab, Restore on it — the kebab hides whatever doesn't apply.
    onSuspendActive:
      canEdit && !archivedTab
        ? (form) =>
            startAction(
              [form.id],
              form.status === 'suspended' ? FormBulkAction.ACTIVE : FormBulkAction.SUSPEND,
            )
        : undefined,
    onArchive:
      canEdit && !archivedTab
        ? (form) => startAction([form.id], FormBulkAction.ARCHIVE)
        : undefined,
    onRestore:
      canEdit && archivedTab ? (form) => startAction([form.id], FormBulkAction.RESTORE) : undefined,
  });

  // DataTable takes a Set of ids; the bar reads the rows themselves.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  // Edit and Duplicate apply to one form only.
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;
  // The bar's Suspend/Activate need a HOMOGENEOUS selection (legacy parity):
  // Suspend only when every picked form is active, Activate only when every one
  // is suspended — a mixed pick offers neither (Archive stays). The Map stores
  // whole rows precisely so this survives paging.
  const selectedRows = useMemo(() => [...selected.values()], [selected]);
  const allActive = selectedRows.every((f) => f.status === 'active');
  const allSuspended = selectedRows.every((f) => f.status === 'suspended');

  const switchTab = (value: string) => {
    setActiveTab(value as FormTab);
    setPage(1);
    setSearch('');
    setFilters(initialFilters(value as FormTab));
    setDateRanges({});
    setSelected(new Map());
    setFolderSel({ kind: 'all' });
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((f) => f.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });

  // Header select-all — scoped to the current page's rows.
  const allSelected = items.length > 0 && items.every((f) => selected.has(f.id));
  const someSelected = items.some((f) => selected.has(f.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((f) => next.delete(f.id));
      else items.forEach((f) => next.set(f.id, f));
      return next;
    });

  // Column-header click — same key toggles direction, new key resets to desc.
  const handleSort = (key: string) =>
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );

  return (
    <div className='forms-page'>
      <header className='forms-page__header'>
        <div>
          <h1 className='forms-page__title'>{t('forms_title')}</h1>
          <p className='forms-page__subtitle'>{t('forms_subtitle')}</p>
        </div>
        <div className='forms-page__header-actions'>
          {canAccessResponses && (
            <Button
              variant='secondary'
              iconStart='list-checks'
              onClick={() => setResponsesOpen(true)}
            >
              {t('forms_action_responses')}
            </Button>
          )}
          {canViewWorkflows && (
            <Button variant='secondary' iconStart='workflow' onClick={() => setWorkflowOpen(true)}>
              {t('forms_action_workflow')}
            </Button>
          )}
          {/* The archive is read-only for these three — you don't create or
              import into it. Responses/Workflow stay on every tab. */}
          {activeTab !== 'archived' && (
            <>
              {canCreate && (
                <>
                  <input
                    ref={importInputRef}
                    type='file'
                    accept='.midani.json'
                    hidden
                    onChange={onPickImportFile}
                  />
                  <Button
                    variant='secondary'
                    iconStart='upload'
                    loading={importing}
                    onClick={() => importInputRef.current?.click()}
                  >
                    {t('forms_action_import')}
                  </Button>
                </>
              )}
              {canExportForms && (
                <Button
                  variant='secondary'
                  iconStart='download'
                  loading={exporting}
                  onClick={handleExport}
                  disabled={activeTab === 'bookmarks' && selected.size === 0}
                >
                  {t('forms_action_export')}
                </Button>
              )}
              {canCreate && (
                <Button iconStart='plus' onClick={() => setCreateOpen(true)}>
                  {t('forms_action_add')}
                </Button>
              )}
            </>
          )}
        </div>
      </header>

      <Tabs
        aria-label={t('forms_title')}
        value={activeTab}
        onChange={switchTab}
        items={[
          {
            value: 'forms',
            label: t('forms_tab_forms'),
            icon: 'clipboard-list',
            count: activeTab === 'forms' ? total : undefined,
          },
          {
            value: 'bookmarks',
            label: t('forms_tab_bookmarks'),
            icon: 'bookmark',
            // Badge only while active: `total` counts the currently fetched set.
            count: bookmarksTab ? total : undefined,
          },
          { value: 'archived', label: t('forms_tab_archived'), icon: 'archive' },
        ]}
      />

      <div className='forms-page__layout'>
        {foldersOpen && (
          <FormFoldersPanel
            folders={folders}
            loading={foldersLoading}
            error={foldersError}
            allCount={total}
            selected={folderSel}
            onSelect={setFolderSel}
            onManage={canEdit ? () => setManageOpen(true) : undefined}
          />
        )}

        <div className='forms-page__main'>
          <div className='forms-page__toolbar'>
            <div className='forms-page__toolbar-lead'>
              <Button
                className={`forms-page__folders-toggle${foldersOpen ? ' is-active' : ''}`}
                variant='secondary'
                iconStart='panel-left'
                aria-pressed={foldersOpen}
                onClick={() => setFoldersOpen((v) => !v)}
              >
                {t('forms_folders_toggle')}
              </Button>
              <div className='forms-page__search'>
                <Input
                  size='sm'
                  iconStart='search'
                  placeholder={t('forms_search_ph')}
                  aria-label={t('field_search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <IconButton
                    className='forms-page__search-clear'
                    icon='x'
                    variant='ghost'
                    size='sm'
                    aria-label={t('forms_search_clear')}
                    onClick={() => setSearch('')}
                  />
                )}
              </div>
              <FilterDropdown
                label={t('action_filter')}
                categories={filterCategories}
                value={filters}
                ranges={dateRanges}
                onApply={(next, ranges) => {
                  // The draft is keyed off the VISIBLE categories — backfill
                  // hidden ones (Status on Archived) with empty sets.
                  setFilters({ ...emptyFilters(), ...next });
                  setDateRanges(ranges);
                }}
                emptyText={t('text_no_results_found')}
              />
            </div>
          </div>

          <Card padded={false} className='forms-page__panel'>
            {selected.size > 0 && (
              <div className='forms-page__selection-bar'>
                <div className='forms-page__selection-lead'>
                  <span className='forms-page__selected'>
                    {t('forms_selected_count', { count: selected.size })}
                  </span>
                  <span className='forms-page__selection-divider' />
                  {/* Edit and Duplicate target one form — hidden while 2+ are picked. */}
                  {onlySelected && (
                    <>
                      {canEdit && (
                        <Button
                          variant='ghost'
                          iconStart='pencil'
                          onClick={() => openBuilder(onlySelected)}
                        >
                          {t('forms_action_edit')}
                        </Button>
                      )}
                      {canCreate && !archivedTab && (
                        <Button
                          variant='ghost'
                          iconStart='copy'
                          disabled={duplicating}
                          onClick={() => runDuplicate(onlySelected, true)}
                        >
                          {t('forms_action_duplicate')}
                        </Button>
                      )}
                    </>
                  )}
                  {canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='folder-input'
                      onClick={() =>
                        setMoveTarget({ forms: [...selected.values()], fromSelection: true })
                      }
                    >
                      {t('forms_action_move_folder')}
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    iconStart={bookmarksTab ? 'bookmark-x' : 'bookmark'}
                    disabled={bookmarking}
                    onClick={() => runBookmark([...selected.keys()], true, !bookmarksTab)}
                  >
                    {t(bookmarksTab ? 'forms_action_unbookmark' : 'forms_action_bookmark')}
                  </Button>
                  {canEdit && archivedTab && (
                    <Button
                      variant='ghost'
                      iconStart='archive-restore'
                      disabled={acting}
                      onClick={() => startAction([...selected.keys()], FormBulkAction.RESTORE)}
                    >
                      {t('forms_action_restore')}
                    </Button>
                  )}
                  {canEdit && !archivedTab && (
                    <>
                      {allActive && (
                        <Button
                          variant='ghost'
                          iconStart='circle-pause'
                          disabled={acting}
                          onClick={() => startAction([...selected.keys()], FormBulkAction.SUSPEND)}
                        >
                          {t('forms_action_suspend')}
                        </Button>
                      )}
                      {allSuspended && (
                        <Button
                          variant='ghost'
                          iconStart='circle-play'
                          disabled={acting}
                          onClick={() => startAction([...selected.keys()], FormBulkAction.ACTIVE)}
                        >
                          {t('forms_action_activate')}
                        </Button>
                      )}
                      <Button
                        variant='ghost'
                        iconStart='archive'
                        disabled={acting}
                        onClick={() => startAction([...selected.keys()], FormBulkAction.ARCHIVE)}
                      >
                        {t('forms_action_archive')}
                      </Button>
                    </>
                  )}
                  {canDelete && archivedTab && (
                    <Button
                      variant='ghost'
                      className='forms-page__selection-delete'
                      iconStart='trash-2'
                      disabled={deleting}
                      onClick={() => setConfirmDelete({ ids: [...selected.keys()] })}
                    >
                      {t('forms_action_delete')}
                    </Button>
                  )}
                </div>
                <Button variant='ghost' onClick={() => setSelected(new Map())}>
                  {t('button_clear')}
                </Button>
              </div>
            )}

            <DataTable
              columns={columns}
              rows={items}
              rowKey={(f) => f.id}
              selectable
              selectedIds={selectedIdSet}
              onToggleSelect={toggleSelect}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggleSelectAll={toggleSelectAll}
              selectAllLabel={t('forms_select_all')}
              onRowClick={canEdit ? openBuilder : undefined}
              sort={sort}
              onSort={handleSort}
              loading={isLoading || isFetching}
              error={isError}
              loadingText={t('text_loading')}
              errorText={t('error_generic')}
              minWidth={1400}
              empty={
                <>
                  <Icon name={bookmarksTab ? 'bookmark' : 'clipboard-list'} size={26} />
                  {t(bookmarksTab ? 'forms_bookmarks_empty' : 'forms_empty')}
                </>
              }
            />

            <TableFooter
              page={page}
              limit={limit}
              total={total}
              limits={[8, 10, 20]}
              onPage={setPage}
              onLimit={(n) => {
                setLimit(n);
                setPage(1);
              }}
              perPageLabel={t('forms_items_per_page')}
              rangeText={t('forms_range', { from, to, total })}
            />
          </Card>
        </div>
      </div>

      {canCreate && <CreateFormModal open={createOpen} onClose={() => setCreateOpen(false)} />}

      {moveTarget && (
        <MoveToFolderModal
          open
          forms={moveTarget.forms}
          folders={folders}
          loading={moving}
          onClose={() => setMoveTarget(null)}
          onConfirm={runMove}
        />
      )}
      {/* Suspend/Archive confirm — stays open on failure (runAction reports). */}
      {confirmAction && (
        <ConfirmModal
          open
          onClose={() => setConfirmAction(null)}
          onConfirm={async () => {
            if (await runAction(confirmAction.ids, confirmAction.action)) setConfirmAction(null);
          }}
          tone='warning'
          icon={confirmAction.action === FormBulkAction.ARCHIVE ? 'archive' : 'circle-pause'}
          title={t(
            confirmAction.action === FormBulkAction.ARCHIVE
              ? 'forms_archive_confirm_title'
              : 'forms_suspend_confirm_title',
            { count: confirmAction.ids.length },
          )}
          description={t(
            confirmAction.action === FormBulkAction.ARCHIVE
              ? 'forms_archive_confirm_body'
              : 'forms_suspend_confirm_body',
            { count: confirmAction.ids.length },
          )}
          confirmLabel={t(
            confirmAction.action === FormBulkAction.ARCHIVE
              ? 'forms_action_archive'
              : 'forms_action_suspend',
          )}
          loading={acting}
        />
      )}
      {/* Delete confirm — one dialog for both paths (the endpoint is bulk, so a
          single row and a whole selection are the same call). Stays open on
          failure (runDelete reports). */}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={async () => {
            if (await runDelete(confirmDelete.ids)) setConfirmDelete(null);
          }}
          tone='danger'
          icon='trash-2'
          title={t('forms_delete_confirm_title', { count: confirmDelete.ids.length })}
          description={t('forms_delete_confirm_body', { count: confirmDelete.ids.length })}
          confirmLabel={t('forms_action_delete')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}
      {canViewWorkflows && (
        <FormWorkflowModal open={workflowOpen} onClose={() => setWorkflowOpen(false)} />
      )}
      {canAccessResponses && (
        <FormResponsesModal open={responsesOpen} onClose={() => setResponsesOpen(false)} />
      )}
      {canEdit && <FormFoldersModal open={manageOpen} onClose={() => setManageOpen(false)} />}
    </div>
  );
};

export default FormTemplatesPage;
